/**
 * The review queues through GET /api/reviews/* (M150): one request per page
 * load replaces the page's list, its /count and the reads the page labelled it
 * with in the browser — every user profile (the first 500 / 1000), one
 * GET /properties/:id per suggestion, one file list per broker submission, the
 * listings origins, the visible comp projects (the first 500) — and, for broker
 * submissions, one signed-URL request per file. Server side:
 * api src/data/review-queues.ts (rows, columns and order of every list).
 *
 * Keys sit under each table's own list / detail prefix
 * (`[<table>, 'list', 'review-queue', …]`), so the invalidations the pages
 * already make (`fieldChangeSuggestionsKeys.all`, `brokerSubmissionsKeys.all`,
 * `reviewWorkflowsKeys.lists()` / `.detail(id)`) still refetch them. The
 * labels come from other tables (user profiles, properties, comp projects,
 * files) whose keys never reach them, so main.tsx's MutationCache invalidates
 * `reviewQueuesKeys.roots` after every successful mutation (the RP-3 / RP-5
 * pattern for composed reads).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../../http-client';

type Row = Record<string, any>;

export interface ProfileLabel {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  trig_user_email: string | null;
}
export interface PropertyLabel {
  id: string;
  full_address: string | null;
  address_line_1: string | null;
  name: string | null;
}
export interface SuggestionsQueueData {
  total: number;
  rows: Row[];
  profiles: ProfileLabel[];
  properties: PropertyLabel[];
}
export interface BrokerSubmissionsQueueData {
  total: number;
  rows: Row[];
  files: Row[];
  profiles: ProfileLabel[];
  origins: Array<{ broker_submission_id: string; agreement_id: string; agreement_name: string }>;
  /** File id → its signed URL (1 hour; research tier only), every file of the page signed in one call. */
  file_urls: Record<string, string>;
}
export interface WorkflowQueueData {
  /** Every workflow, past the 500-row window. */
  total: number;
  /** Not COMPLETED (the page's Open filter), past the window. */
  open: number;
  rows: Row[];
  statuses: Array<{ id: string; code: string | null; name: string | null }>;
  projects: Array<{ id: string; title: string | null }>;
  profiles: ProfileLabel[];
}
export interface WorkflowPageData {
  workflow: Row;
  decisions: Row[];
  project: { id: string; title: string | null } | null;
  profiles: ProfileLabel[];
}

export const reviewQueuesKeys = {
  roots: [
    ['field_change_suggestions', 'list', 'review-queue'],
    ['broker_submissions', 'list', 'review-queue'],
    ['review_workflows', 'list', 'review-queue'],
    ['review_workflows', 'detail', 'review-page'],
  ] as const,
  suggestions: (status: string, limit: number, offset: number) =>
    ['field_change_suggestions', 'list', 'review-queue', { status, limit, offset }] as const,
  brokerSubmissions: (submissionStatusId: string | null, limit: number, offset: number) =>
    ['broker_submissions', 'list', 'review-queue', { submissionStatusId, limit, offset }] as const,
  workflows: () => ['review_workflows', 'list', 'review-queue'] as const,
  workflow: (id: string) => ['review_workflows', 'detail', 'review-page', id] as const,
};

const query = (params: Record<string, string | number | null>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== null) q.set(k, String(v));
  return q.toString();
};

/** id → row. */
export function byId<R extends { id: string }>(rows: readonly R[] | undefined): Map<string, R> {
  return new Map((rows ?? []).map((r) => [r.id, r]));
}

/**
 * Keep the previous page's data while the next page of the SAME filter loads
 * (its `total` keeps the pager, as the old per-filter /count did); a new filter
 * starts empty, as the old count's new key did.
 */
const sameFilter = (name: string, value: unknown) =>
  <T,>(previous: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined => {
    const prev = previousQuery?.queryKey[3] as Record<string, unknown> | undefined;
    return prev && name in prev && prev[name] === value ? previous : undefined;
  };

/**
 * /reviews/submissions: one page of suggestions of `status` ('all' = every status).
 * `enabled` false holds the request (the dashboard's Review queue counts card without feature:reviews).
 */
export function useSuggestionsQueue(status: string, limit: number, offset: number, enabled = true) {
  const q = useQuery<SuggestionsQueueData>({
    enabled,
    queryKey: reviewQueuesKeys.suggestions(status, limit, offset),
    queryFn: async () => (await httpClient.get<{ data: SuggestionsQueueData }>(`/reviews/suggestions?${query({ status, limit, offset })}`)).data,
    placeholderData: sameFilter('status', status),
  });
  const current = q.isPlaceholderData ? undefined : q.data;
  const profileById = useMemo(() => byId(current?.profiles), [current]);
  const propertyById = useMemo(() => byId(current?.properties), [current]);
  return {
    rows: current?.rows ?? [],
    total: q.data?.total,
    profileById,
    propertyById,
    isLoading: q.isLoading || q.isPlaceholderData,
  };
}

/**
 * /reviews/broker-submissions: one page with `submission_status_id` (null = every submission).
 * `enabled` false holds the request (the page waits for the SUBMITTED status id).
 */
export function useBrokerSubmissionsQueue(submissionStatusId: string | null, limit: number, offset: number, enabled = true) {
  const q = useQuery<BrokerSubmissionsQueueData>({
    enabled,
    queryKey: reviewQueuesKeys.brokerSubmissions(submissionStatusId, limit, offset),
    queryFn: async () =>
      (
        await httpClient.get<{ data: BrokerSubmissionsQueueData }>(
          `/reviews/broker-submissions?${query({ submission_status_id: submissionStatusId, limit, offset })}`,
        )
      ).data,
    placeholderData: sameFilter('submissionStatusId', submissionStatusId),
  });
  const current = q.isPlaceholderData ? undefined : q.data;
  const derived = useMemo(() => {
    const filesBySubmission = new Map<string, Row[]>();
    for (const f of current?.files ?? []) {
      const list = filesBySubmission.get(f.broker_submission_id) ?? [];
      list.push(f);
      filesBySubmission.set(f.broker_submission_id, list);
    }
    return {
      filesBySubmission,
      userById: byId(current?.profiles),
      agreementBySubmission: new Map((current?.origins ?? []).map((o) => [o.broker_submission_id, o.agreement_name])),
      fileUrls: current?.file_urls ?? NO_FILE_URLS,
    };
  }, [current]);
  return {
    rows: current?.rows ?? [],
    total: q.data?.total,
    ...derived,
    isLoading: q.isLoading || q.isPlaceholderData,
    isError: q.isError,
  };
}

const NO_FILE_URLS: Record<string, string> = {};

/**
 * /reviews: the newest 500 workflows with their status, project title and starter, and the total / open counts.
 * `enabled` false holds the request (the dashboard's Review queue counts card without feature:reviews).
 */
export function useWorkflowQueue(enabled = true) {
  const q = useQuery<WorkflowQueueData>({
    enabled,
    queryKey: reviewQueuesKeys.workflows(),
    queryFn: async () => (await httpClient.get<{ data: WorkflowQueueData }>('/reviews/workflows')).data,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, refetch: q.refetch };
}

/** /reviews/:id: the workflow, its decisions, its project (when visible) and the people named. */
export function useWorkflowPage(id: string) {
  const q = useQuery<WorkflowPageData>({
    queryKey: reviewQueuesKeys.workflow(id),
    queryFn: async () => (await httpClient.get<{ data: WorkflowPageData }>(`/reviews/workflows/${encodeURIComponent(id)}`)).data,
    enabled: !!id,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, refetch: q.refetch };
}
